// src/pages/FormsPage.tsx
import { useEffect, useState, useRef } from "react";
import { useFormsStore } from "@/store/formsStore";
import { useAuthStore } from "@/store/authStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import {
  Plus,
  Trash2,
  Pencil,
  Copy,
  Loader2,
  MessageSquare,
  ImagePlus,
  FileDown,
  Sparkles,
} from "lucide-react";
import type { Form, FormField, FieldType } from "@/types/form";
import { cn } from "@/lib/utils";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  addDoc,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, storage } from "@/lib/firebase";
import { regionsData } from "@/lib/russiaRegions";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  ImageRun,
} from "docx";
import { saveAs } from "file-saver";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import CreateFormFromReportDialog from "@/components/forms/CreateFormFromReportDialog";

const FIELD_TYPES: { type: FieldType; label: string }[] = [
  { type: "text", label: "Короткий текст" },
  { type: "textarea", label: "Длинный текст" },
  { type: "number", label: "Число" },
  { type: "date", label: "Дата" },
  { type: "select", label: "Выпадающий список" },
  { type: "radio", label: "Радиокнопки" },
  { type: "checkbox", label: "Чекбоксы" },
  { type: "file", label: "Загрузка файла/фото" },
  { type: "phone", label: "Телефон" },
  { type: "email", label: "Email" },
  { type: "link", label: "Ссылка" },
  { type: "vk_link", label: "Ссылка ВК" },
  { type: "section", label: "Заголовок раздела" },
];

// 1x1 прозрачный png
const EMPTY_PNG_B64 =
  "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==";

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

interface FormResponse {
  id: string;
  formId: string;
  answers: Record<string, any>;
  submittedAt: any;
}

interface ManualPhoto {
  id: string;
  formId: string;
  regionId: string;
  regionName: string;
  url: string;
  storagePath: string;
  caption?: string;
  uploadedAt: any;
  uploadedBy: number;
}

interface DisplayPhoto {
  url: string;
  title: string;
  subtitle?: string;
  source: "response" | "manual";
}

const regionsList = Object.values(regionsData).sort((a, b) =>
  a.name.localeCompare(b.name, "ru")
);

async function urlToArrayBuffer(url: string): Promise<ArrayBuffer | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToUint8Array(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function toKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[^a-zа-я0-9]+/gi, "_")
    .replace(/^_|_$/g, "");
}

function findAnswer(
  fields: FormField[],
  answers: Record<string, any>,
  ...keywords: string[]
): any {
  for (const field of fields) {
    const label = (field.label || "").toLowerCase();
    if (keywords.some((k) => label.includes(k.toLowerCase()))) {
      const val = answers[field.id];
      if (val !== undefined && val !== null && val !== "") return val;
    }
  }
  return undefined;
}

function normalizeRegionName(name: string): string {
  return (name || "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/область|обл\.?|республика|край|г\.\s*/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

export default function FormsPage() {
  const { user } = useAuthStore();
  const {
    forms,
    loading,
    init,
    cleanup,
    createForm,
    updateForm,
    deleteForm,
    setFormStatus,
  } = useFormsStore();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<Form | null>(null);
  const [fromReportOpen, setFromReportOpen] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState<FormField[]>([]);
  const [saving, setSaving] = useState(false);

  const [responsesOpen, setResponsesOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [responses, setResponses] = useState<FormResponse[]>([]);
  const [loadingResponses, setLoadingResponses] = useState(false);

  const [photosOpen, setPhotosOpen] = useState(false);
  const [photoForm, setPhotoForm] = useState<Form | null>(null);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [allDisplayPhotos, setAllDisplayPhotos] = useState<DisplayPhoto[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [generatingReport, setGeneratingReport] = useState<string | null>(null);

  useEffect(() => {
    init();
    return () => cleanup();
  }, [init, cleanup]);

  useEffect(() => {
    if (!selectedForm || !responsesOpen) return;

    setLoadingResponses(true);
    const q = query(
      collection(db, "formResponses"),
      where("formId", "==", selectedForm.id),
      orderBy("submittedAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const list: FormResponse[] = snapshot.docs.map((d) => ({
          id: d.id,
          ...(d.data() as any),
        }));
        setResponses(list);
        setLoadingResponses(false);
      },
      (err) => {
        console.error("Ошибка загрузки ответов:", err);
        setLoadingResponses(false);
      }
    );

    return () => unsub();
  }, [selectedForm, responsesOpen]);

  useEffect(() => {
    if (!photoForm || !photosOpen) return;

    const loadAllPhotos = async () => {
      setLoadingPhotos(true);
      const result: DisplayPhoto[] = [];

      try {
        const responsesSnap = await getDocs(
          query(collection(db, "formResponses"), where("formId", "==", photoForm.id))
        );

        responsesSnap.docs.forEach((docSnap, idx) => {
          const data = docSnap.data();
          const answers = data.answers || {};

          (photoForm.fields || []).forEach((field) => {
            if (field.type === "file") {
              const urls = answers[field.id];
              if (Array.isArray(urls)) {
                urls.forEach((url: string) => {
                  result.push({
                    url,
                    title: `Ответ #${idx + 1}`,
                    subtitle: field.label,
                    source: "response",
                  });
                });
              }
            }
          });
        });

        try {
          const photosSnap = await getDocs(
            query(
              collection(db, "formManualPhotos"),
              where("formId", "==", photoForm.id)
            )
          );

          photosSnap.docs.forEach((docSnap) => {
            const p = docSnap.data() as ManualPhoto;
            result.push({
              url: p.url,
              title: p.regionName,
              subtitle: p.caption || undefined,
              source: "manual",
            });
          });
        } catch (e) {
          console.warn("Не удалось загрузить ручные фото:", e);
        }

        setAllDisplayPhotos(result);
      } catch (e) {
        console.error(e);
        toast({
          title: "Ошибка",
          description: "Не удалось загрузить фото",
          variant: "destructive",
        });
      } finally {
        setLoadingPhotos(false);
      }
    };

    loadAllPhotos();
  }, [photoForm, photosOpen]);

  const openCreate = () => {
    setEditingForm(null);
    setTitle("");
    setDescription("");
    setFields([]);
    setIsCreateOpen(true);
  };

  const openEdit = (form: Form) => {
    setEditingForm(form);
    setTitle(form.title);
    setDescription(form.description || "");
    setFields(form.fields || []);
    setIsCreateOpen(true);
  };

  const openResponses = (form: Form) => {
    setSelectedForm(form);
    setResponsesOpen(true);
  };

  const openPhotos = (form: Form) => {
    setPhotoForm(form);
    setSelectedRegion("");
    setCaption("");
    setPhotosOpen(true);
  };

  const addField = (type: FieldType) => {
    const newField: FormField = {
      id: generateId(),
      type,
      label:
        type === "section"
          ? "Новый раздел"
          : FIELD_TYPES.find((f) => f.type === type)?.label || "Новое поле",
      required: false,
      placeholder: "",
    };

    if (type === "select" || type === "radio" || type === "checkbox") {
      newField.options = ["Вариант 1", "Вариант 2"];
    }

    setFields((prev) => [...prev, newField]);
  };

  const updateField = (id: string, data: Partial<FormField>) => {
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...data } : f))
    );
  };

  const removeField = (id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast({
        title: "Ошибка",
        description: "Введите название формы",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);

    try {
      if (editingForm) {
        const ok = await updateForm(editingForm.id, {
          title: title.trim(),
          description: description.trim(),
          fields,
        });
        if (ok) {
          toast({ title: "Форма обновлена" });
          setIsCreateOpen(false);
        } else {
          toast({
            title: "Ошибка",
            description: "Не удалось сохранить",
            variant: "destructive",
          });
        }
      } else {
        const id = await createForm({
          title: title.trim(),
          description: description.trim(),
          fields,
        });
        if (id) {
          toast({ title: "Форма создана" });
          setIsCreateOpen(false);
        } else {
          toast({
            title: "Ошибка",
            description: "Не удалось создать форму",
            variant: "destructive",
          });
        }
      }
    } catch (e) {
      console.error(e);
      toast({
        title: "Ошибка",
        description: "Произошла ошибка при сохранении",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Удалить эту форму? Все ответы тоже пропадут.")) return;
    const ok = await deleteForm(id);
    if (ok) toast({ title: "Форма удалена" });
  };

  const handleStatusChange = async (id: string, status: Form["status"]) => {
    const ok = await setFormStatus(id, status);
    if (ok) {
      toast({
        title:
          status === "active"
            ? "Форма активирована"
            : status === "closed"
            ? "Форма закрыта"
            : "Статус изменён",
      });
    }
  };

  const copyPublicLink = (id: string) => {
    const url = `${window.location.origin}/f/${id}`;
    navigator.clipboard.writeText(url);
    toast({ title: "Ссылка скопирована", description: url });
  };

  const handleUploadPhotos = async () => {
    if (!photoForm || !selectedRegion) {
      toast({
        title: "Ошибка",
        description: "Выберите регион",
        variant: "destructive",
      });
      return;
    }

    const files = fileInputRef.current?.files;
    if (!files || files.length === 0) {
      toast({
        title: "Ошибка",
        description: "Выберите хотя бы одно фото",
        variant: "destructive",
      });
      return;
    }

    const region = regionsData[selectedRegion];
    if (!region) return;

    setUploading(true);

    try {
      for (const file of Array.from(files)) {
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const storagePath = `forms/${photoForm.id}/manual/${selectedRegion}/${timestamp}_${safeName}`;

        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        await addDoc(collection(db, "formManualPhotos"), {
          formId: photoForm.id,
          regionId: selectedRegion,
          regionName: region.name,
          url,
          storagePath,
          caption: caption.trim() || null,
          uploadedAt: Timestamp.now(),
          uploadedBy: Number(user?.id) || user?.numericId || 0,
        });
      }

      toast({
        title: "Готово!",
        description: `Загружено фото: ${files.length}`,
      });

      setSelectedRegion("");
      setCaption("");
      if (fileInputRef.current) fileInputRef.current.value = "";

      setPhotosOpen(false);
      setTimeout(() => openPhotos(photoForm), 300);
    } catch (e) {
      console.error(e);
      toast({
        title: "Ошибка",
        description: "Не удалось загрузить фото",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  // ===== ГЕНЕРАЦИЯ ОТЧЁТА =====
  const handleGenerateReport = async (form: Form) => {
    setGeneratingReport(form.id);
    toast({
      title: "Собираем отчёт...",
      description: "Это может занять несколько секунд",
    });

    try {
      const responsesSnap = await getDocs(
        query(collection(db, "formResponses"), where("formId", "==", form.id))
      );
      const allResponses: FormResponse[] = responsesSnap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as any),
      }));

      const formFields = form.fields || [];
      const numberFields = formFields.filter((f) => f.type === "number");

      const sums: Record<string, number> = {};
      numberFields.forEach((f) => {
        sums[f.id] = 0;
      });

      allResponses.forEach((resp) => {
        numberFields.forEach((f) => {
          const val = Number(resp.answers?.[f.id]);
          if (!isNaN(val)) sums[f.id] += val;
        });
      });

      let manualPhotosList: ManualPhoto[] = [];
      try {
        const photosSnap = await getDocs(
          query(collection(db, "formManualPhotos"), where("formId", "==", form.id))
        );
        manualPhotosList = photosSnap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as any),
        }));
      } catch (e) {
        console.warn("Не удалось загрузить ручные фото:", e);
      }

      const regions: any[] = [];

      for (let idx = 0; idx < allResponses.length; idx++) {
        const resp = allResponses[idx];
        const answers = resp.answers || {};

        const regionName =
          findAnswer(
            formFields,
            answers,
            "региональное отделение",
            "регион",
            "отделение"
          ) || `Регион ${idx + 1}`;

        const points =
          Number(
            findAnswer(formFields, answers, "точек поздравления", "точек", "точки")
          ) || 0;

        const volunteers =
          Number(
            findAnswer(formFields, answers, "добровольц", "волонтёр", "волонтер")
          ) || 0;

        const beneficiaries =
          Number(findAnswer(formFields, answers, "благополучател", "получател")) ||
          0;

        const locationsRaw = findAnswer(
          formFields,
          answers,
          "места проведения",
          "места",
          "адрес",
          "локац"
        );
        const locations = Array.isArray(locationsRaw)
          ? locationsRaw.join("\n")
          : String(locationsRaw || "—");

        const photoUrls: string[] = [];

        formFields.forEach((f) => {
          if (f.type === "file") {
            const urls = answers[f.id];
            if (Array.isArray(urls)) {
              urls.forEach((url: string) => {
                if (url) photoUrls.push(url);
              });
            }
          }
        });

        const normName = normalizeRegionName(String(regionName));
        manualPhotosList.forEach((p) => {
          const pName = normalizeRegionName(p.regionName);
          if (pName.includes(normName) || normName.includes(pName)) {
            if (p.url) photoUrls.push(p.url);
          }
        });

        // base64, макс 4 фото
        const photoBase64: string[] = [];
        for (const url of photoUrls.slice(0, 4)) {
          const buf = await urlToArrayBuffer(url);
          if (buf && buf.byteLength > 0) {
            photoBase64.push(arrayBufferToBase64(buf));
          }
        }

        // Пары: по 2 фото в ряд
        const photoRows: { a: string; b: string }[] = [];
        for (let i = 0; i < photoBase64.length; i += 2) {
          photoRows.push({
            a: photoBase64[i],
            b: photoBase64[i + 1] || "",
          });
        }

        regions.push({
          index: idx + 1,
          regionName: String(regionName),
          points,
          volunteers,
          beneficiaries,
          locations,
          photoRows,
          photos: photoBase64.map((image) => ({ image })),
        });
      }

      // Нормализация адресов
      try {
        const itemsToNormalize = regions
          .filter((r) => r.locations && r.locations !== "—")
          .map((r) => ({
            regionName: r.regionName,
            locations: r.locations,
          }));

        if (itemsToNormalize.length > 0) {
          toast({
            title: "Нормализуем адреса...",
            description: "Нейросеть приводит адреса к единому формату",
          });

          const functions = getFunctions(undefined, "us-central1");
          const normalizeFn = httpsCallable(functions, "normalizeLocations");

          const normResponse = await normalizeFn({ items: itemsToNormalize });
          const results = (normResponse.data as any)?.results || [];

          let resultIdx = 0;
          for (let i = 0; i < regions.length; i++) {
            if (regions[i].locations && regions[i].locations !== "—") {
              const normalized = results.find(
                (r: any) => r.index === resultIdx + 1
              );
              if (normalized?.locations) {
                regions[i].locations = normalized.locations;
              }
              resultIdx++;
            }
          }
        }
      } catch (normError) {
        console.warn("Нормализация адресов не удалась:", normError);
      }

      const templateData: Record<string, any> = {
        title: form.title,
        description: form.description || "",
        date: new Date().toLocaleString("ru-RU"),
        totalResponses: allResponses.length,
        totalRegions: allResponses.length,
        regions,
      };

      numberFields.forEach((f) => {
        const key = toKey(f.label);
        templateData[key] = sums[f.id] || 0;
        templateData[f.id] = sums[f.id] || 0;

        const label = f.label.toLowerCase();
        if (label.includes("добровольц")) templateData.totalVolunteers = sums[f.id] || 0;
        if (label.includes("благополучател"))
          templateData.totalBeneficiaries = sums[f.id] || 0;
        if (label.includes("точек") || label.includes("точки")) {
          templateData.totalPoints = sums[f.id] || 0;
        }
      });

      if (templateData.totalVolunteers === undefined) {
        templateData.totalVolunteers = regions.reduce(
          (s, r) => s + r.volunteers,
          0
        );
      }
      if (templateData.totalBeneficiaries === undefined) {
        templateData.totalBeneficiaries = regions.reduce(
          (s, r) => s + r.beneficiaries,
          0
        );
      }
      if (templateData.totalPoints === undefined) {
        templateData.totalPoints = regions.reduce((s, r) => s + r.points, 0);
      }

      const fileName = `Отчёт_${form.title.replace(/[\\/:*?"<>|]/g, "_")}_${new Date()
        .toISOString()
        .slice(0, 10)}.docx`;

      // ===== Заполнение шаблона =====
      if (form.templateUrl) {
        try {
          toast({ title: "Заполняем шаблон..." });

          const response = await fetch(form.templateUrl);
          if (!response.ok) throw new Error("Не удалось скачать шаблон");

          const templateBuffer = await response.arrayBuffer();
          const hasPhotos = regions.some(
            (r) => r.photoRows && r.photoRows.length > 0
          );

          const renderWithTemplate = (
            data: Record<string, any>,
            withImages: boolean
          ) => {
            const zip = new PizZip(templateBuffer);

            const options: any = {
              paragraphLoop: true,
              linebreaks: true,
              delimiters: { start: "{", end: "}" },
            };

            if (withImages) {
              const imageModule = new ImageModule({
                centered: false,
                fileType: "docx",
                getImage: (tagValue: any) => {
                  const b64 =
                    tagValue &&
                    typeof tagValue === "string" &&
                    tagValue.length > 100
                      ? tagValue
                      : EMPTY_PNG_B64;
                  return base64ToUint8Array(b64);
                },
                getSize: (_img: any, tagValue: any) => {
                  if (
                    !tagValue ||
                    typeof tagValue !== "string" ||
                    tagValue.length < 100
                  ) {
                    return [1, 1];
                  }
                  return [343, 229]; // 9.07 × 6.05 см
                },
              });
              options.modules = [imageModule];
            } else {
              data = {
                ...data,
                regions: (data.regions || []).map((r: any) => ({
                  ...r,
                  photoRows: [],
                  photos: [],
                })),
              };
            }

            const doc = new Docxtemplater(zip, options);
            doc.render(data);

            return doc.getZip().generate({
              type: "blob",
              mimeType:
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            });
          };

          let out: Blob;
          try {
            out = renderWithTemplate(templateData, hasPhotos);
          } catch (imgErr) {
            console.warn("Вставка фото не удалась, пробуем без фото:", imgErr);
            out = renderWithTemplate(templateData, false);
          }

          saveAs(out, fileName);

          toast({
            title: "Отчёт готов!",
            description: "Заполнен твой шаблон",
          });
          return;
        } catch (templateError) {
          console.warn("Не удалось заполнить шаблон:", templateError);
          toast({
            title: "Шаблон не заполнен",
            description: "Генерируем обычный отчёт",
          });
        }
      }

      // ===== Fallback =====
      const children: Paragraph[] = [];

      children.push(
        new Paragraph({
          text: form.title,
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
        })
      );

      if (form.description) {
        children.push(
          new Paragraph({
            text: form.description,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
          })
        );
      }

      children.push(
        new Paragraph({
          text: `Дата формирования: ${new Date().toLocaleString("ru-RU")}`,
          spacing: { after: 200 },
        })
      );

      children.push(
        new Paragraph({
          text: `Всего ответов: ${allResponses.length}`,
          spacing: { after: 400 },
        })
      );

      if (numberFields.length > 0) {
        children.push(
          new Paragraph({
            text: "Сводные показатели",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 200 },
          })
        );

        numberFields.forEach((f) => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: `${f.label}: `, bold: true }),
                new TextRun({ text: String(sums[f.id] || 0) }),
              ],
              spacing: { after: 100 },
            })
          );
        });
      }

      children.push(
        new Paragraph({
          text: "Детальный отчёт по регионам",
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        })
      );

      for (const r of regions) {
        children.push(
          new Paragraph({
            text: `${r.index}. ${r.regionName}`,
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 200, after: 100 },
          })
        );
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Точек поздравления: ", bold: true }),
              new TextRun({ text: String(r.points) }),
            ],
            spacing: { after: 40 },
          })
        );
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Добровольцев: ", bold: true }),
              new TextRun({ text: String(r.volunteers) }),
            ],
            spacing: { after: 40 },
          })
        );
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Благополучателей: ", bold: true }),
              new TextRun({ text: String(r.beneficiaries) }),
            ],
            spacing: { after: 40 },
          })
        );
        if (r.locations && r.locations !== "—") {
          children.push(
            new Paragraph({
              children: [new TextRun({ text: "Места проведения: ", bold: true })],
              spacing: { after: 40 },
            })
          );
          children.push(
            new Paragraph({
              text: r.locations,
              spacing: { after: 100 },
            })
          );
        }

        if (r.photos && r.photos.length > 0) {
          children.push(
            new Paragraph({
              text: "Фотографии:",
              spacing: { before: 100, after: 80 },
            })
          );
          for (const photo of r.photos) {
            children.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    data: base64ToUint8Array(photo.image),
                    transformation: { width: 343, height: 229 },
                    type: "jpg",
                  }),
                ],
                spacing: { after: 120 },
              })
            );
          }
        }
      }

      const doc = new Document({
        sections: [{ properties: {}, children }],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, fileName);

      toast({
        title: "Отчёт готов!",
        description: `Скачан файл: ${fileName}`,
      });
    } catch (e) {
      console.error(e);
      toast({
        title: "Ошибка",
        description: "Не удалось сформировать отчёт",
        variant: "destructive",
      });
    } finally {
      setGeneratingReport(null);
    }
  };

  const statusColor = (status: Form["status"]) => {
    if (status === "active") return "bg-green-600";
    if (status === "closed") return "bg-red-600";
    return "bg-slate-600";
  };

  const statusLabel = (status: Form["status"]) => {
    if (status === "active") return "Активна";
    if (status === "closed") return "Закрыта";
    return "Черновик";
  };

  const formatDate = (ts: any) => {
    if (!ts) return "—";
    try {
      const date = ts.toDate ? ts.toDate() : new Date(ts);
      return date.toLocaleString("ru-RU");
    } catch {
      return "—";
    }
  };

  return (
    <div className="p-8 md:p-10 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
        <h1 className="text-4xl md:text-5xl font-bold text-white bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
          Акции / Формы
        </h1>

        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => setFromReportOpen(true)}
            variant="outline"
            className="border-purple-500 text-purple-200 text-lg px-6 py-6 rounded-2xl"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            Создать из отчёта
          </Button>

          <Button
            onClick={openCreate}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-lg px-8 py-6 rounded-2xl shadow-xl"
          >
            <Plus className="h-6 w-6 mr-2" />
            Создать форму
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-12 w-12 animate-spin text-purple-400" />
        </div>
      ) : forms.length === 0 ? (
        <div className="text-center py-24 text-purple-300 text-xl">
          Пока нет ни одной формы.
          <br />
          Нажмите «Создать форму» или «Создать из отчёта».
        </div>
      ) : (
        <div className="grid gap-6">
          {forms.map((form) => (
            <div
              key={form.id}
              className="bg-purple-950/50 border border-purple-700/50 rounded-3xl p-6 md:p-8 backdrop-blur-xl shadow-xl"
            >
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-3">
                    <h2 className="text-2xl font-bold text-white">{form.title}</h2>
                    <Badge className={cn("text-white", statusColor(form.status))}>
                      {statusLabel(form.status)}
                    </Badge>
                  </div>
                  {form.description && (
                    <p className="text-purple-300 mb-4">{form.description}</p>
                  )}
                  <div className="flex flex-wrap gap-4 text-sm text-purple-400">
                    <span>Полей: {form.fields?.length || 0}</span>
                    <span>Ответов: {form.responsesCount || 0}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Select
                    value={form.status}
                    onValueChange={(v) =>
                      handleStatusChange(form.id, v as Form["status"])
                    }
                  >
                    <SelectTrigger className="w-[140px] bg-purple-900/50 border-purple-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Черновик</SelectItem>
                      <SelectItem value="active">Активна</SelectItem>
                      <SelectItem value="closed">Закрыта</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openResponses(form)}
                    className="border-purple-500 text-purple-200"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Ответы
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openPhotos(form)}
                    className="border-purple-500 text-purple-200"
                  >
                    <ImagePlus className="h-4 w-4 mr-1" />
                    Фото
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleGenerateReport(form)}
                    disabled={generatingReport === form.id}
                    className="border-green-600 text-green-300 hover:bg-green-900/30"
                  >
                    {generatingReport === form.id ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <FileDown className="h-4 w-4 mr-1" />
                    )}
                    Собрать отчёт
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyPublicLink(form.id)}
                    className="border-purple-500 text-purple-200"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Ссылка
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(form)}
                    className="border-purple-500 text-purple-200"
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Редактировать
                  </Button>

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(form.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-950 border-purple-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              {editingForm ? "Редактирование формы" : "Новая форма"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Название акции / формы *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Например: В благодарность за службу! (8 марта)"
                className="bg-purple-950/50 border-purple-600 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label>Описание (необязательно)</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Кратко опишите, что нужно заполнить..."
                className="bg-purple-950/50 border-purple-600 text-white min-h-[80px]"
              />
            </div>

            <div className="space-y-4">
              <Label className="text-lg">Поля формы</Label>

              {fields.length === 0 && (
                <p className="text-purple-400 text-sm">
                  Пока нет полей. Добавьте первое поле кнопками ниже.
                </p>
              )}

              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="bg-purple-950/40 border border-purple-700/60 rounded-2xl p-5 space-y-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3">
                        <span className="text-purple-400 text-sm font-mono">
                          #{index + 1}
                        </span>
                        <Badge variant="secondary">
                          {FIELD_TYPES.find((t) => t.type === field.type)?.label}
                        </Badge>
                      </div>

                      <Input
                        value={field.label}
                        onChange={(e) =>
                          updateField(field.id, { label: e.target.value })
                        }
                        placeholder="Название поля"
                        className="bg-slate-900 border-purple-600 text-white"
                      />

                      {field.type !== "section" && (
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) =>
                              updateField(field.id, {
                                required: e.target.checked,
                              })
                            }
                            className="w-5 h-5 accent-purple-500 cursor-pointer"
                          />
                          <Label>Обязательное поле</Label>
                        </div>
                      )}

                      {(field.type === "select" ||
                        field.type === "radio" ||
                        field.type === "checkbox") && (
                        <div className="space-y-2">
                          <Label className="text-sm text-purple-300">
                            Варианты ответов (каждый с новой строки)
                          </Label>
                          <Textarea
                            value={(field.options || []).join("\n")}
                            onChange={(e) =>
                              updateField(field.id, {
                                options: e.target.value
                                  .split("\n")
                                  .map((s) => s.trim())
                                  .filter(Boolean),
                              })
                            }
                            className="bg-slate-900 border-purple-600 text-white min-h-[80px]"
                          />
                        </div>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeField(field.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-950/50"
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-purple-800 pt-6">
              <Label className="mb-4 block text-purple-300">Добавить поле:</Label>
              <div className="flex flex-wrap gap-2">
                {FIELD_TYPES.map((ft) => (
                  <Button
                    key={ft.type}
                    variant="outline"
                    size="sm"
                    onClick={() => addField(ft.type)}
                    className="border-purple-600 text-purple-200 hover:bg-purple-800/50"
                  >
                    {ft.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setIsCreateOpen(false)}
              className="border-purple-600 text-purple-200"
            >
              Отмена
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-purple-600 to-pink-600"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingForm ? "Сохранить изменения" : "Создать форму"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={responsesOpen} onOpenChange={setResponsesOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-slate-950 border-purple-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              Ответы: {selectedForm?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {loadingResponses ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-10 w-10 animate-spin text-purple-400" />
              </div>
            ) : responses.length === 0 ? (
              <div className="text-center py-12 text-purple-300">
                Пока нет ни одного ответа
              </div>
            ) : (
              <div className="space-y-6">
                <p className="text-purple-300">
                  Всего ответов: <strong>{responses.length}</strong>
                </p>

                {responses.map((resp, idx) => (
                  <div
                    key={resp.id}
                    className="bg-purple-950/40 border border-purple-700/50 rounded-2xl p-5"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-purple-400 text-sm">
                        Ответ #{responses.length - idx}
                      </span>
                      <span className="text-purple-400 text-sm">
                        {formatDate(resp.submittedAt)}
                      </span>
                    </div>

                    <div className="space-y-3">
                      {selectedForm?.fields
                        ?.filter((f) => f.type !== "section")
                        .map((field) => {
                          const value = resp.answers?.[field.id];
                          let displayValue = "—";
                          if (
                            value !== undefined &&
                            value !== null &&
                            value !== ""
                          ) {
                            displayValue = Array.isArray(value)
                              ? value.join(", ")
                              : String(value);
                          }

                          return (
                            <div key={field.id} className="text-sm">
                              <span className="text-purple-300">
                                {field.label}:
                              </span>{" "}
                              <span className="text-white font-medium">
                                {displayValue}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResponsesOpen(false)}
              className="border-purple-600 text-purple-200"
            >
              Закрыть
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={photosOpen} onOpenChange={setPhotosOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-950 border-purple-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl">
              Фото — {photoForm?.title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-8 py-4">
            <div>
              <h3 className="text-lg font-semibold text-purple-200 mb-4">
                Все фото ({allDisplayPhotos.length})
              </h3>

              {loadingPhotos ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                </div>
              ) : allDisplayPhotos.length === 0 ? (
                <p className="text-purple-400 text-sm py-6">
                  Пока нет загруженных фото
                </p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {allDisplayPhotos.map((photo, idx) => (
                    <div
                      key={idx}
                      className="bg-purple-950/50 border border-purple-700/50 rounded-2xl overflow-hidden"
                    >
                      <img
                        src={photo.url}
                        alt={photo.title}
                        className="w-full h-40 object-cover"
                      />
                      <div className="p-3 space-y-1">
                        <p className="text-sm font-medium text-white truncate">
                          {photo.title}
                        </p>
                        {photo.subtitle && (
                          <p className="text-xs text-purple-300 truncate">
                            {photo.subtitle}
                          </p>
                        )}
                        <p className="text-xs text-purple-500">
                          {photo.source === "manual" ? "Ручное" : "Из ответа"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-purple-800 pt-6">
              <h3 className="text-lg font-semibold text-purple-200 mb-4">
                Добавить фото по региону
              </h3>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Регион *</Label>
                  <Select
                    value={selectedRegion}
                    onValueChange={setSelectedRegion}
                  >
                    <SelectTrigger className="bg-purple-950/50 border-purple-600 text-white">
                      <SelectValue placeholder="Выберите регион" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {regionsList.map((r) => (
                        <SelectItem key={r.isoCode} value={r.isoCode}>
                          {r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Фото *</Label>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="bg-purple-950/50 border-purple-600 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Подпись (необязательно)</Label>
                  <Input
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Например: Торжественное мероприятие"
                    className="bg-purple-950/50 border-purple-600 text-white"
                  />
                </div>

                <Button
                  onClick={handleUploadPhotos}
                  disabled={uploading}
                  className="bg-gradient-to-r from-purple-600 to-pink-600"
                >
                  {uploading && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Загрузить фото
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPhotosOpen(false)}
              className="border-purple-600 text-purple-200"
            >
              Закрыть
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateFormFromReportDialog
        open={fromReportOpen}
        onOpenChange={setFromReportOpen}
      />
    </div>
  );
}